import type {
  PublishObservedStage,
  PublishFlowStageDetailFunnel,
} from '@/services/publishFlowObservabilityService'
import type {
  PublishFlowInventorySyncProvenance,
  PublishFlowInventorySyncRuleEvidence,
} from '@/services/publishFlowDecisionService'

/**
 * Top-level Publish Flow business sections (Preview-aligned mental model).
 * Underlying technical stage keys are unchanged.
 */
export type PublishFlowBusinessSectionId =
  | 'eligibilityCheck'
  | 'inventorySync'
  | 'fieldMapping'
  | 'vehicleGroupings'
  | 'publishRulesEvaluation'

export const PUBLISH_FLOW_BUSINESS_SECTIONS: ReadonlyArray<{
  id: PublishFlowBusinessSectionId
  subStages: readonly PublishObservedStage[]
}> = [
  {
    id: 'eligibilityCheck',
    subStages: ['preProcessor'],
  },
  {
    // Field Mapping (and its nested Feed Field Rules) runs during SRP creation
    // over the feed-changed upsert set, BEFORE Inventory Sync decides which VINs
    // continue or are held back. The dealer-facing order mirrors that execution
    // order, so Field Mapping sits before Inventory Sync.
    id: 'fieldMapping',
    subStages: ['fieldMapping'],
  },
  {
    // Inventory Sync reconciles the feed-prepared candidates against
    // CurrentInventory and produces the continued / held-back split. It runs
    // AFTER Field Mapping / Feed Field Rules, so held-back VINs can legitimately
    // carry mapping and feed-field-rule records (they were processed before the
    // hold-back decision).
    id: 'inventorySync',
    subStages: ['inventorySync'],
  },
  {
    id: 'vehicleGroupings',
    subStages: ['groups'],
  },
  {
    id: 'publishRulesEvaluation',
    subStages: ['listingRules'],
  },
] as const

export function friendlyPublishFlowBusinessSectionLabel(id: PublishFlowBusinessSectionId): string {
  switch (id) {
    case 'eligibilityCheck':
      return 'Feed Preparation & Eligibility'
    case 'inventorySync':
      return 'Inventory Sync'
    case 'fieldMapping':
      return 'Field Mapping'
    case 'vehicleGroupings':
      return 'Vehicle Groupings'
    case 'publishRulesEvaluation':
      return 'Publish Rules Evaluation'
    default:
      return 'Publish Stage'
  }
}

export type StageDetailStatus =
  | 'available'
  | 'empty'
  | 'notConfigured'
  | 'skipped'
  | 'unsupported'
  | 'error'

export type StageRollupSignal =
  | 'captured'
  | 'measured'
  | 'skipped'
  | 'notConfigured'
  | 'error'
  | 'underDevelopment'

export type StageRollupCardInput = {
  stage: PublishObservedStage
  detailStatus: StageDetailStatus
  hasDetails: boolean
  statusLabel: string
}

export type BusinessSectionRollupContext = {
  preProcessorMeasured: boolean
  visibilityMeasured: boolean
}

export function stageRollupSignal(
  card: StageRollupCardInput,
  ctx: BusinessSectionRollupContext,
): StageRollupSignal {
  if (card.statusLabel === 'Needs attention' || card.detailStatus === 'error') return 'error'
  if (card.statusLabel === 'Checked') return 'captured'
  if (card.statusLabel === 'No changes') return 'measured'

  if (card.hasDetails && card.statusLabel !== 'No changes') return 'captured'

  if (card.stage === 'preProcessor' && ctx.preProcessorMeasured) return 'measured'
  if (card.stage === 'listingRules' && ctx.visibilityMeasured) return 'measured'

  if (['All passed', 'Checked'].includes(card.statusLabel)) return 'measured'
  if (card.detailStatus === 'skipped' || card.statusLabel === 'Not needed') return 'skipped'
  if (card.detailStatus === 'notConfigured' || card.statusLabel === 'Not configured') {
    return 'notConfigured'
  }
  if (card.detailStatus === 'available' || card.detailStatus === 'empty') return 'measured'
  if (card.detailStatus === 'unsupported' || card.statusLabel === 'Not captured yet') {
    return 'underDevelopment'
  }
  return 'underDevelopment'
}

export function aggregateBusinessSectionStatus(signals: StageRollupSignal[]): {
  label: string
  color: string
} {
  if (signals.length === 0) return { label: 'Not captured yet', color: 'grey' }
  if (signals.some((s) => s === 'error')) return { label: 'Needs attention', color: 'error' }
  if (signals.some((s) => s === 'captured')) return { label: 'Checked', color: 'success' }
  if (signals.some((s) => s === 'measured')) return { label: 'Checked', color: 'success' }
  if (signals.every((s) => s === 'skipped')) return { label: 'Not needed', color: 'grey' }
  if (signals.every((s) => s === 'notConfigured')) return { label: 'Not configured', color: 'grey' }
  if (signals.every((s) => s === 'underDevelopment'))
    return { label: 'Not captured yet', color: 'grey' }
  if (signals.every((s) => s === 'skipped' || s === 'notConfigured'))
    return { label: 'Not needed', color: 'grey' }
  return { label: 'Checked', color: 'info' }
}

export type BusinessSectionSummaryContext = {
  eligibilityCheck: {
    sourceVinCount: number | null
    eligibleForMappingCount: number | null
    /** Number of feeds that contributed source VINs (multi-primary runs). */
    participatingFeedCount?: number | null
    /**
     * Whether `sourceVinCount` is a publish-wide deduped unique total across
     * feeds. Currently always `false`/absent: we only have per-feed unique
     * counts, not a deduped union, so multi-feed copy must not claim
     * uniqueness.
     */
    sourceVinCountIsDeduped?: boolean
    /**
     * Sum of per-feed source VIN counts (raw, may overlap). Multi-feed only;
     * used for the "N source VINs from M feeds were prepared" headline.
     */
    rawSourceTotal?: number | null
    /**
     * Deduped unique VINs that passed Feed Preparation across all source feeds,
     * sourced from the canonical pass-through artifact record count
     * (`manifest.preProcessorPassThrough.recordCount`). This is the SAME number
     * the View VINs modal totals to. Used for the multi-feed
     * "N unique VINs passed Feed Preparation" line. It is NOT a downstream
     * publish-rules / evaluated count — that belongs to Publish Rules Evaluation.
     */
    uniquePassThroughCount?: number | null
    /** Dealer-facing feed label for single-feed preparation summaries. */
    feedDisplayName?: string | null
  }
  /**
   * Reconciled Inventory Sync funnel slice (authoritative). `null`/absent when
   * the stage was not captured for this run (older runs / non-reconciled
   * paths) — the section then renders an honest "not captured" state. Optional
   * so callers that predate Inventory Sync stay valid.
   */
  inventorySync?: {
    funnel: PublishFlowStageDetailFunnel | null
  }
  fieldMapping: {
    vehiclesChecked: number | null
    mappingDecisionsCaptured: number | null
  }
  vehicleGroupings: {
    vehiclesEvaluated: number | null
    changedCount: number | null
  }
  publishRulesEvaluation: {
    vehiclesEvaluated: number | null
    accepted: number | null
    blocked: number | null
  }
}

function formatCount(n: number, singular: string, plural?: string): string {
  const safe = Math.max(0, Math.floor(n))
  const noun = safe === 1 ? singular : (plural ?? `${singular}s`)
  return `${safe} ${noun}`
}

export function buildBusinessSectionSummaryLine(
  sectionId: PublishFlowBusinessSectionId,
  ctx: BusinessSectionSummaryContext,
): string | null {
  switch (sectionId) {
    case 'eligibilityCheck': {
      const {
        sourceVinCount,
        participatingFeedCount,
      } = ctx.eligibilityCheck
      // Multi-feed runs: `sourceVinCount`/`eligibleForMappingCount` are a single
      // feed's slice, not a deduped publish-wide total, so we must NOT present
      // that per-feed number as an across-feeds headline. Instead surface the raw
      // per-feed source total (sum, may overlap); the deduped unique count is
      // carried by the bridge + Publish Eligibility.
      if (typeof participatingFeedCount === 'number' && participatingFeedCount > 1) {
        const feeds = Math.floor(participatingFeedCount)
        const raw = ctx.eligibilityCheck.rawSourceTotal
        if (typeof raw === 'number' && raw >= 0) {
          return `${formatCount(raw, 'source VIN')} from ${feeds} feeds were prepared.`
        }
        return `Source VINs from ${feeds} feeds were prepared.`
      }
      if (typeof sourceVinCount !== 'number') {
        return null
      }
      const source = Math.max(0, Math.floor(sourceVinCount))
      const feed = ctx.eligibilityCheck.feedDisplayName?.trim()
      const fromFeed = feed ? ` from ${feed}` : ''
      return `${formatCount(source, 'source VIN')}${fromFeed} were prepared.`
    }
    case 'inventorySync': {
      const vm = buildInventorySyncCardViewModel(ctx.inventorySync?.funnel ?? null)
      return vm.summaryLine
    }
    case 'fieldMapping': {
      const { mappingDecisionsCaptured } = ctx.fieldMapping
      if (typeof mappingDecisionsCaptured !== 'number') return null
      const decisions = Math.max(0, Math.floor(mappingDecisionsCaptured))
      if (decisions === 0) return 'No field changes.'
      const isMultiFeed =
        typeof ctx.eligibilityCheck.participatingFeedCount === 'number' &&
        ctx.eligibilityCheck.participatingFeedCount > 1
      if (isMultiFeed) {
        return `${formatCount(decisions, 'field mapping decision')} recorded for feed-attributed vehicles.`
      }
      return `${formatCount(decisions, 'field mapping decision')} recorded.`
    }
    case 'vehicleGroupings': {
      const { changedCount } = ctx.vehicleGroupings
      if (typeof changedCount !== 'number') return null
      const changed = Math.max(0, Math.floor(changedCount))
      if (changed === 0) return 'No vehicles changed groups.'
      return `${formatCount(changed, 'vehicle')} changed groups.`
    }
    case 'publishRulesEvaluation': {
      const { vehiclesEvaluated, accepted, blocked } = ctx.publishRulesEvaluation
      if (typeof vehiclesEvaluated !== 'number') return null
      const evaluated = Math.max(0, Math.floor(vehiclesEvaluated))
      const matched = typeof blocked === 'number' ? Math.max(0, Math.floor(blocked)) : null
      const acceptedCount = typeof accepted === 'number' ? Math.max(0, Math.floor(accepted)) : null
      const isMultiFeed =
        typeof ctx.eligibilityCheck.participatingFeedCount === 'number' &&
        ctx.eligibilityCheck.participatingFeedCount > 1
      // Multi-feed runs: make the input explicit (the deduped unique evaluated
      // population) alongside accepted/blocked, so the collapsed row reconciles
      // with the raw per-feed totals shown upstream.
      if (isMultiFeed && matched !== null && acceptedCount !== null) {
        return `${evaluated} unique ${
          evaluated === 1 ? 'VIN' : 'VINs'
        } evaluated: ${acceptedCount} accepted, ${matched} blocked.`
      }
      if (matched === 0) return `All ${formatCount(evaluated, 'evaluated vehicle')} passed publish rules.`
      if (matched !== null && acceptedCount !== null) {
        return `${formatCount(matched, 'vehicle')} matched blocking rules; ${acceptedCount} accepted.`
      }
      return `${formatCount(evaluated, 'vehicle')} evaluated.`
    }
    default:
      return null
  }
}

/* -------------------------------------------------------------------------- */
/* Inventory Sync card presentation helpers                                    */
/* -------------------------------------------------------------------------- */

/**
 * Drill-down bucket selector for the Inventory Sync card. `continued` remains
 * part of the read API contract, but the UI intentionally does not render a
 * continued VIN drill-down; continued stays visible as a count only.
 */
export type InventorySyncDrillDownBucket =
  | 'continued'
  | 'held_back'
  | 'admitted_outside_feed_prep'
  | 'removed'
  | 'reconciled_out'
  | 'anomaly'

/** Funnel breakdown line shown inside the expanded Inventory Sync card. */
export type InventorySyncFunnelLine = {
  id: 'continued' | 'heldBack' | 'admittedOutsideFeedPrep'
  label: string
  count: number
}

/** Held-back reason bucket, decorated with a dealer-readable label. */
export type InventorySyncReasonBucketView = {
  code: string
  label: string
  count: number
}

/** Drill-down action descriptor. Zero-count buckets are flagged, never dropped. */
export type InventorySyncDrillDownAction = {
  bucket: InventorySyncDrillDownBucket
  label: string
  count: number
  /**
   * True when the action has no records this run. The UI hides these by
   * default but keeps them supported (e.g. behind a "show empty" toggle), so a
   * zero bucket can still be opened to confirm "0 records".
   */
  hiddenByDefault: boolean
}

export type InventorySyncCardViewModel = {
  /** Whether a reconciled funnel slice was captured for this run. */
  hasFunnel: boolean
  /** Headline: "630 feed-prepared VINs reconciled". */
  headline: string
  /** Collapsed-row summary; null when the funnel was not captured. */
  summaryLine: string | null
  intakeCount: number
  continuedCount: number
  heldBackCount: number
  admittedOutsideFeedPrepCount: number
  handoffCount: number
  removedCount: number
  reconciledOutCount: number
  anomalyCount: number
  /** Expanded funnel lines (admitted line omitted when zero). */
  funnelLines: InventorySyncFunnelLine[]
  /** Handoff line: "491 vehicles moved to Vehicle Groupings." */
  handoffLine: string
  /** Held-back reason buckets (the backend never emits zero-count buckets). */
  heldBackReasonBuckets: InventorySyncReasonBucketView[]
  /**
   * When true, the expanded card shows a compact held-back reason breakdown
   * beneath the funnel line (multiple distinct reasons only).
   */
  showHeldBackReasonBreakdown: boolean
  /** Drill-down actions in display order; continued omitted, zero buckets hidden. */
  drillDownActions: InventorySyncDrillDownAction[]
  /** Honest note shown when the reconciled funnel was not captured. */
  notCapturedNote: string | null
}

const INVENTORY_SYNC_HELD_BACK_REASON_LABELS: Record<string, string> = {
  archived_returning_still_blocked_by_snapshot_guardrail:
    'Archived & returning, still blocked by Publish Rules',
  not_in_downstream_candidate_set: 'Not in the downstream candidate set',
}

const INVENTORY_SYNC_DRILL_DOWN_LABELS: Record<InventorySyncDrillDownBucket, string> = {
  continued: 'Continued',
  held_back: 'Held back',
  admitted_outside_feed_prep: 'Admitted outside Feed Preparation',
  removed: 'Removed from inventory',
  reconciled_out: 'Reconciled out',
  anomaly: 'Anomalies',
}

/** Convert a snake_case reason code into a readable fallback label. */
function humanizeReasonCode(code: string): string {
  const cleaned = code.trim().replace(/[_\s]+/g, ' ')
  if (cleaned.length === 0) return 'Other'
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
}

function inventorySyncReasonLabel(code: string): string {
  return INVENTORY_SYNC_HELD_BACK_REASON_LABELS[code] ?? humanizeReasonCode(code)
}

/** Build the expanded-card held-back funnel line from count + reason buckets. */
function buildHeldBackFunnelLineLabel(
  heldBack: number,
  buckets: InventorySyncReasonBucketView[],
): string {
  if (buckets.length === 0) {
    return `${heldBack} held back.`
  }
  if (buckets.length === 1) {
    const [bucket] = buckets
    if (bucket.code === 'archived_returning_still_blocked_by_snapshot_guardrail') {
      return `${heldBack} held back because they are archived and still blocked by Publish Rules.`
    }
    return `${heldBack} held back: ${bucket.label}.`
  }
  return `${heldBack} held back`
}

/**
 * Dealer-readable matched-rule display for a held-back VIN's drill-down row.
 *
 *   - `captured`    — a specific Publish Rule was identified for this VIN.
 *   - `notCaptured` — the held-back cause is known but the specific rule was
 *     not captured (`ruleProvenanceAvailable === false`); the UI shows honest
 *     fallback copy instead of pretending a rule applied.
 *   - `none`        — no rule context applies (e.g. continued / removed rows).
 *
 * Per-VIN by construction: the label/detail are built from THIS VIN's evidence,
 * never a generic rule applied across the bucket.
 */
export type InventorySyncRuleDisplay =
  | { kind: 'captured'; label: string; actualValueSummary: string | null }
  | { kind: 'notCaptured'; label: string }
  | { kind: 'none' }

/** Copy shown when a held-back VIN has no captured rule evidence. */
export const INVENTORY_SYNC_RULE_NOT_CAPTURED_COPY =
  'Rule details were not captured for this vehicle.'

/** Render a bounded scalar rule value; non-scalars render empty (never dumped). */
function formatInventorySyncRuleValue(value: unknown): string {
  if (typeof value === 'string') return value.trim()
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

function inventorySyncRuleLabel(rule: PublishFlowInventorySyncRuleEvidence): string {
  const name = rule.ruleName?.trim()
  if (name) return cleanInventorySyncRuleLabel(name)
  const id = rule.ruleId?.trim()
  if (id) return cleanInventorySyncRuleLabel(humanizeReasonCode(id))
  const reason = rule.ruleReason?.trim()
  if (reason) return cleanInventorySyncRuleLabel(humanizeReasonCode(reason))
  return 'Publish Rule'
}

function cleanInventorySyncRuleLabel(label: string): string {
  const cleaned = label.trim().replace(/^\[auto:feed-mapping\]\s*/i, '')
  if (cleaned.length === 0) return label.trim()
  return cleaned
    .replace(/\bfdds2\b/gi, 'FDDS')
    .replace(/\bfdds\b/gi, 'FDDS')
    .replace(/\bvauto\b/gi, 'vAuto')
}

function inventorySyncActualValueSummary(rule: PublishFlowInventorySyncRuleEvidence): string | null {
  const actual = formatInventorySyncRuleValue(rule.actualValue)
  if (actual) return actual
  return null
}

function hasMeaningfulRule(rule: PublishFlowInventorySyncRuleEvidence): boolean {
  return Boolean(
    rule.ruleId?.trim() ||
      rule.ruleName?.trim() ||
      rule.ruleReason?.trim() ||
      rule.field?.trim(),
  )
}

export function describeInventorySyncRule(
  provenance: PublishFlowInventorySyncProvenance | null | undefined,
): InventorySyncRuleDisplay {
  const rule = provenance?.rule
  if (rule && hasMeaningfulRule(rule)) {
    return {
      kind: 'captured',
      label: inventorySyncRuleLabel(rule),
      actualValueSummary: inventorySyncActualValueSummary(rule),
    }
  }
  if (provenance?.ruleProvenanceAvailable === false) {
    return { kind: 'notCaptured', label: INVENTORY_SYNC_RULE_NOT_CAPTURED_COPY }
  }
  return { kind: 'none' }
}

/**
 * Build the Inventory Sync card view-model from the reconciled funnel slice
 * (`manifest.stages.inventorySync.details.funnel`). This is the AUTHORITATIVE
 * source; the card never recomputes counts from decision records.
 *
 * Display rules:
 *   - intake → "{n} feed-prepared VINs reconciled".
 *   - continued → "{n} continued as downstream inventory candidates".
 *   - heldBack → "{n} held back" (merged with the sole reason when exactly one
 *     bucket exists; trailing period when no reason buckets exist).
 *   - admittedOutsideFeedPrep → "{n} admitted outside Feed Preparation"
 *     (OMITTED from the funnel lines when zero).
 *   - handoff → "{n} vehicles moved to Vehicle Groupings."
 *     (never implementation-stage naming).
 *   - held-back reason buckets render as a compact breakdown only when multiple
 *     distinct reasons exist.
 *   - drill-down actions cover all six buckets; zero-count buckets are flagged
 *     `hiddenByDefault` but remain supported; continued is count-only.
 */
export function buildInventorySyncCardViewModel(
  funnel: PublishFlowStageDetailFunnel | null | undefined,
): InventorySyncCardViewModel {
  if (!funnel) {
    return {
      hasFunnel: false,
      headline: 'Inventory Sync',
      summaryLine: null,
      intakeCount: 0,
      continuedCount: 0,
      heldBackCount: 0,
      admittedOutsideFeedPrepCount: 0,
      handoffCount: 0,
      removedCount: 0,
      reconciledOutCount: 0,
      anomalyCount: 0,
      funnelLines: [],
      handoffLine: '',
      heldBackReasonBuckets: [],
      showHeldBackReasonBreakdown: false,
      drillDownActions: [],
      notCapturedNote: 'Inventory Sync reconciliation was not captured for this run.',
    }
  }

  const intake = nonNegativeInt(funnel.intakeCount)
  const continued = nonNegativeInt(funnel.continuedCount)
  const heldBack = nonNegativeInt(funnel.heldBackCount)
  const admitted = nonNegativeInt(funnel.admittedOutsideFeedPrepCount)
  const handoff = nonNegativeInt(funnel.handoffCount)
  const removed = nonNegativeInt(funnel.removedCount)
  const reconciledOut = nonNegativeInt(funnel.reconciledOutCount)
  const anomaly = nonNegativeInt(funnel.anomalyCount)

  const heldBackReasonBuckets: InventorySyncReasonBucketView[] = (
    funnel.heldBackReasonBuckets ?? []
  )
    .map((bucket) => ({
      code: bucket.code,
      label: inventorySyncReasonLabel(bucket.code),
      count: nonNegativeInt(bucket.count),
    }))
    .filter((bucket) => bucket.count > 0)

  const showHeldBackReasonBreakdown = heldBackReasonBuckets.length > 1

  const funnelLines: InventorySyncFunnelLine[] = [
    {
      id: 'continued',
      label: `${continued} continued as downstream inventory ${
        continued === 1 ? 'candidate' : 'candidates'
      }`,
      count: continued,
    },
    {
      id: 'heldBack',
      label: buildHeldBackFunnelLineLabel(heldBack, heldBackReasonBuckets),
      count: heldBack,
    },
  ]
  // Admitted-outside-Feed-Prep is surfaced only when positive (hidden if zero).
  if (admitted > 0) {
    funnelLines.push({
      id: 'admittedOutsideFeedPrep',
      label: `${admitted} admitted outside Feed Preparation`,
      count: admitted,
    })
  }

  const drillDownCounts: Record<Exclude<InventorySyncDrillDownBucket, 'continued'>, number> = {
    held_back: heldBack,
    admitted_outside_feed_prep: admitted,
    removed,
    reconciled_out: reconciledOut,
    anomaly,
  }
  const drillDownActions: InventorySyncDrillDownAction[] = (
    Object.keys(drillDownCounts) as Array<Exclude<InventorySyncDrillDownBucket, 'continued'>>
  ).map((bucket) => ({
    bucket,
    label: INVENTORY_SYNC_DRILL_DOWN_LABELS[bucket],
    count: drillDownCounts[bucket],
    hiddenByDefault: drillDownCounts[bucket] === 0,
  }))

  const summaryParts = [
    `${continued} continued`,
    `${heldBack} held back`,
  ]
  if (admitted > 0) summaryParts.push(`${admitted} admitted outside Feed Preparation`)

  return {
    hasFunnel: true,
    headline: `${intake} feed-prepared ${intake === 1 ? 'VIN' : 'VINs'} reconciled`,
    summaryLine: `${intake} feed-prepared ${
      intake === 1 ? 'VIN' : 'VINs'
    } reconciled · ${summaryParts.join(', ')}.`,
    intakeCount: intake,
    continuedCount: continued,
    heldBackCount: heldBack,
    admittedOutsideFeedPrepCount: admitted,
    handoffCount: handoff,
    removedCount: removed,
    reconciledOutCount: reconciledOut,
    anomalyCount: anomaly,
    funnelLines,
    handoffLine: `${handoff} ${
      handoff === 1 ? 'vehicle' : 'vehicles'
    } moved to Vehicle Groupings.`,
    heldBackReasonBuckets,
    showHeldBackReasonBreakdown,
    drillDownActions,
    notCapturedNote: null,
  }
}

/* -------------------------------------------------------------------------- */
/* Website Publish & Refresh (final stage) presentation helpers                */
/* -------------------------------------------------------------------------- */

/* ------------------------------ Website Visibility Changes ----------------- */

/**
 * Live website outcome deltas for this publish. These are the existing Run
 * Summary outcome values (added/removed/updated/made visible/hidden +
 * live-after count) re-presented as a final-stage subsection. No new counts
 * are computed — the source of truth stays the run metrics.
 */
export interface WebsiteVisibilityChangesInput {
  hasOutcome: boolean
  /** Complete aggregate activity counts exist; live-after alone is not evidence. */
  activityComplete: boolean
  liveAfter: number | null
  added: number
  removed: number
  updated: number
  madeVisible: number
  hidden: number
  /**
   * Whether per-VIN visibility-change detail records (with reasons) are
   * available to drill into. The publish flow does not capture per-VIN reasons
   * today, so this is false and the UI shows an honest note instead of a
   * "View changes" button. Never fabricated.
   */
  recordsAvailable?: boolean
  /**
   * Count of UNIQUE VINs that changed, deduped across outcome buckets. When
   * provided, this drives the headline/`changedCount` so a VIN that lands in
   * two buckets (e.g. added + made visible) is counted once. The bucket sum
   * would double-count it. Omitted/`null` when per-VIN lists are unavailable
   * (or truncated), in which case the bucket sum is used as an honest upper
   * bound. The delta breakdown always uses the raw bucket counts.
   */
  uniqueChangedVins?: number | null
}

export interface WebsiteVisibilityChangesViewModel {
  hasOutcome: boolean
  activityComplete: boolean
  /** Null when aggregate activity counts are unavailable. */
  changedCount: number | null
  /** Primary line: unavailable, complete zero, or complete positive activity. */
  headline: string
  /** Full delta breakdown, or null when there were no changes. */
  deltaLine: string | null
  recordsAvailable: boolean
  /** Honest note for incomplete activity or unavailable per-VIN details. */
  detailsNote: string | null
  /** Whether to offer a "View changes" drilldown. */
  showViewChanges: boolean
}

function nonNegativeInt(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
}

/**
 * Build the Website Visibility Changes subsection from existing run outcome
 * values. `changedCount = added + removed + updated + madeVisible + hidden`
 * (sum of outcome buckets; a VIN may appear in more than one bucket).
 */
export function buildWebsiteVisibilityChangesViewModel(
  input: WebsiteVisibilityChangesInput,
): WebsiteVisibilityChangesViewModel {
  if (!input.activityComplete) {
    return {
      hasOutcome: input.hasOutcome,
      activityComplete: false,
      changedCount: null,
      headline: 'Website visibility changes are unavailable for this publish.',
      deltaLine: null,
      recordsAvailable: false,
      detailsNote: 'The metrics for this run are incomplete. Complete totals are unavailable.',
      showViewChanges: false,
    }
  }
  const added = nonNegativeInt(input.added)
  const removed = nonNegativeInt(input.removed)
  const updated = nonNegativeInt(input.updated)
  const madeVisible = nonNegativeInt(input.madeVisible)
  const hidden = nonNegativeInt(input.hidden)
  const bucketSum = added + removed + updated + madeVisible + hidden
  // Prefer the deduped unique-VIN count for the headline when available; the
  // bucket sum double-counts VINs that appear in more than one bucket.
  const hasUnique =
    typeof input.uniqueChangedVins === 'number' && Number.isFinite(input.uniqueChangedVins)
  const changedCount = hasUnique ? nonNegativeInt(input.uniqueChangedVins as number) : bucketSum
  const recordsAvailable = input.recordsAvailable === true

  if (bucketSum === 0) {
    return {
      hasOutcome: input.hasOutcome,
      activityComplete: true,
      changedCount: 0,
      headline: 'No website visibility changes in this publish.',
      deltaLine: null,
      recordsAvailable,
      detailsNote: null,
      showViewChanges: false,
    }
  }

  const deltaLine =
    `${added} added · ${removed} removed · ${updated} updated · ` +
    `${madeVisible} made visible · ${hidden} hidden`

  return {
    hasOutcome: input.hasOutcome,
    activityComplete: true,
    changedCount,
    headline: `${changedCount} ${changedCount === 1 ? 'vehicle' : 'vehicles'} changed on the website.`,
    deltaLine,
    recordsAvailable,
    detailsNote: recordsAvailable
      ? null
      : 'Visibility change details were not captured for this run.',
    showViewChanges: recordsAvailable,
  }
}

/* ------------------------------ Website Refresh ---------------------------- */

export type WebsiteRefreshStatus =
  | 'not_captured'
  | 'skipped'
  | 'submitted'
  | 'completed'
  | 'failed'

export type WebsiteRefreshMechanism = 'wordpress_varnish' | 'cloudfront' | 'unknown'

/**
 * Per-type tally of refreshed cache keys (mirrors the backend
 * `FlowWebsiteRefreshKeyTypeCounts`). Derived from the same deduped records as
 * `keyCount`; absent on legacy runs that predate the field.
 */
export interface WebsiteRefreshKeyTypeCounts {
  vehicle?: number | null
  account?: number | null
  other?: number | null
}

/**
 * Helper copy explaining that the refreshed cache-key count is a property of
 * the website refresh request (one key per VIN + the account key), not the
 * accepted-vehicle count from Publish Eligibility — so the two need not match.
 */
export const WEBSITE_REFRESH_KEYS_HELPER_TEXT =
  'Cache keys come from the website refresh request and may not equal the number of accepted vehicles.'

/**
 * Cache/CDN refresh outcome for a publish run (mirrors the backend
 * `FlowWebsiteRefresh` contract). Neutral across mechanisms; WordPress/Varnish
 * runs expose no path list or invalidation id, so those stay absent. The UI
 * renders real metadata only when present and never invents values.
 */
export interface WebsiteRefreshDetails {
  status: WebsiteRefreshStatus
  mechanism?: WebsiteRefreshMechanism
  startedAt?: string | null
  finishedAt?: string | null
  durationMs?: number | null
  requestId?: string | null
  executionArn?: string | null
  jobId?: string | null
  keyCount?: number | null
  /**
   * Per-type breakdown of the refreshed cache keys (vehicle / account / other).
   * Drives the type-breakdown line on the card; absent on legacy runs.
   */
  keyTypeCounts?: WebsiteRefreshKeyTypeCounts | null
  /**
   * True when a refreshed-key artifact was persisted and is browsable via the
   * `website-refresh-keys` API. Drives whether "View refreshed keys" appears.
   */
  recordsAvailable?: boolean | null
  /**
   * Legacy embedded key list. New runs do NOT populate this (the list moved to
   * a backend artifact); kept only so older persisted metrics still render a
   * count. The dialog never renders from this — it always pages the API.
   */
  keys?: readonly string[] | null
  pathCount?: number | null
  paths?: readonly string[] | null
  message?: string | null
  errorMessage?: string | null
}

export interface WebsiteRefreshMetadataRow {
  label: string
  value: string
}

export interface WebsiteRefreshViewModel {
  status: WebsiteRefreshStatus
  statusLabel: string
  statusColor: 'success' | 'warning' | 'grey' | 'info'
  /** Display lines, already ordered for compact rendering. */
  lines: string[]
  /**
   * Per-type breakdown line (e.g. "500 vehicle page keys · 1 account key"), shown
   * beneath the total when structured type counts are available. Null when the
   * breakdown is unavailable — the card must not invent one.
   */
  keyTypeBreakdown: string | null
  /**
   * Helper copy clarifying that the cache-key count may not equal the accepted
   * vehicle count. Present whenever a key total is shown; null otherwise.
   */
  keysHelperText: string | null
  /** Real, non-key metadata to show (mechanism, ids, timing). */
  metadata: WebsiteRefreshMetadataRow[]
  /**
   * Legacy embedded keys (older runs only). The dialog pages the backend API
   * and does NOT render from this; retained for the summary-count fallback.
   */
  refreshedKeys: readonly string[]
  /** Count of refreshed cache keys (from summary `keyCount`, embedded fallback). */
  refreshedKeyCount: number
  /** Whether the "View refreshed keys" affordance should be shown. */
  showRefreshedKeys: boolean
}

const WEBSITE_REFRESH_STATUSES: ReadonlySet<WebsiteRefreshStatus> = new Set([
  'not_captured',
  'skipped',
  'submitted',
  'completed',
  'failed',
])

function trimmedOrEmpty(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : ''
}

function mechanismLabel(mechanism: WebsiteRefreshMechanism | undefined): string | null {
  switch (mechanism) {
    case 'wordpress_varnish':
      return 'WordPress / Varnish'
    case 'cloudfront':
      return 'CloudFront'
    case 'unknown':
      return 'Unknown'
    default:
      return null
  }
}

function formatDurationMs(durationMs: number | null | undefined): string | null {
  if (typeof durationMs !== 'number' || !Number.isFinite(durationMs) || durationMs < 0) return null
  if (durationMs < 1000) return `${Math.round(durationMs)} ms`
  return `${(durationMs / 1000).toFixed(durationMs < 10000 ? 1 : 0)} s`
}

function cleanedRefreshPaths(paths: readonly string[] | null | undefined): string[] {
  if (!Array.isArray(paths)) return []
  return paths.map((path) => trimmedOrEmpty(path)).filter((path) => path.length > 0)
}

function normalizeKeyCount(keyCount: number | null | undefined): number | null {
  if (typeof keyCount !== 'number' || !Number.isFinite(keyCount) || keyCount < 0) return null
  return Math.floor(keyCount)
}

/**
 * Build the per-type breakdown line from structured key-type counts. Only types
 * with a positive count are included (so "0 vehicle keys" is never shown), and
 * the result is null when no type has a value — the card then shows just the
 * total and never fabricates a breakdown. Uses singular/plural nouns:
 * "1 vehicle page key" vs "2 vehicle page keys".
 */
function buildKeyTypeBreakdown(
  counts: WebsiteRefreshKeyTypeCounts | null | undefined,
): string | null {
  if (!counts || typeof counts !== 'object') return null
  const vehicle = normalizeKeyCount(counts.vehicle)
  const account = normalizeKeyCount(counts.account)
  const other = normalizeKeyCount(counts.other)
  const parts: string[] = []
  if (vehicle && vehicle > 0)
    parts.push(formatCount(vehicle, 'vehicle page key', 'vehicle page keys'))
  if (account && account > 0) parts.push(formatCount(account, 'account key', 'account keys'))
  if (other && other > 0) parts.push(formatCount(other, 'other key', 'other keys'))
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * Summary copy for the refreshed-key count. The all-vehicle phrasing is only
 * possible when the legacy embedded key list is present (older runs); summary-
 * only (new) runs use the neutral "{N} cache keys refreshed." copy because the
 * card no longer receives the full list.
 */
function refreshedKeysSummary(count: number): string {
  const noun = count === 1 ? 'key' : 'keys'
  // Always describe the total as "cache keys" — never "vehicle cache keys" —
  // so the count is not misread as a vehicle count. The vehicle/account split
  // (when available) is carried by the per-type breakdown line.
  return `${count} cache ${noun} refreshed.`
}

/**
 * Build the Website Refresh subsection view-model. Renders real status,
 * metadata, paths, and errors only when present; otherwise returns an honest
 * "not captured" state. Never shows vehicle counts and never invents data.
 */
export function buildWebsiteRefreshViewModel(
  details: WebsiteRefreshDetails | null | undefined,
): WebsiteRefreshViewModel {
  const status = details?.status
  const resolved: WebsiteRefreshStatus =
    status && WEBSITE_REFRESH_STATUSES.has(status) ? status : 'not_captured'
  // Legacy embedded list (older runs only). New runs persist the list to a
  // backend artifact and send only `keyCount` + `recordsAvailable`.
  const refreshedKeys = cleanedRefreshPaths(details?.keys ?? details?.paths)
  const summaryKeyCount = normalizeKeyCount(details?.keyCount)
  const refreshedKeyCount =
    refreshedKeys.length > 0 ? refreshedKeys.length : (summaryKeyCount ?? 0)
  const recordsAvailable = details?.recordsAvailable === true
  const showRefreshedKeys = recordsAvailable || refreshedKeyCount > 0
  const keyTypeBreakdown = buildKeyTypeBreakdown(details?.keyTypeCounts)

  const metadata: WebsiteRefreshMetadataRow[] = []
  if (resolved !== 'not_captured' && details) {
    const mech = mechanismLabel(details.mechanism)
    if (mech) metadata.push({ label: 'Mechanism', value: mech })
    const requestId = trimmedOrEmpty(details.requestId)
    if (requestId) metadata.push({ label: 'Request ID', value: requestId })
    const executionArn = trimmedOrEmpty(details.executionArn)
    if (executionArn) metadata.push({ label: 'Execution ARN', value: executionArn })
    const jobId = trimmedOrEmpty(details.jobId)
    if (jobId) metadata.push({ label: 'Job ID', value: jobId })
    const duration = formatDurationMs(details.durationMs)
    if (duration) metadata.push({ label: 'Duration', value: duration })
  }

  switch (resolved) {
    case 'skipped': {
      const lines = ['Cache refresh was not required for this run.']
      const message = trimmedOrEmpty(details?.message)
      if (message) lines.push(message)
      return {
        status: resolved,
        statusLabel: 'Skipped',
        statusColor: 'grey',
        lines,
        keyTypeBreakdown: null,
        keysHelperText: null,
        metadata,
        refreshedKeys,
        refreshedKeyCount,
        showRefreshedKeys,
      }
    }
    case 'submitted': {
      const lines = ['Cache refresh was submitted. Completion was not captured.']
      const message = trimmedOrEmpty(details?.message)
      if (message) lines.push(message)
      return {
        status: resolved,
        statusLabel: 'Submitted',
        statusColor: 'info',
        lines,
        keyTypeBreakdown: null,
        keysHelperText: null,
        metadata,
        refreshedKeys,
        refreshedKeyCount,
        showRefreshedKeys,
      }
    }
    case 'completed': {
      const lines = ['Cache refresh completed.']
      const hasKeyTotal = refreshedKeyCount > 0
      lines.push(
        hasKeyTotal
          ? refreshedKeysSummary(refreshedKeyCount)
          : 'Refreshed key details were not captured.',
      )
      return {
        status: resolved,
        statusLabel: 'Completed',
        statusColor: 'success',
        lines,
        // Breakdown/helper only accompany a real total. When no structured keys
        // were captured ("Refreshed key details were not captured."), neither is
        // shown so the card never implies counts it does not have.
        keyTypeBreakdown: hasKeyTotal ? keyTypeBreakdown : null,
        keysHelperText: hasKeyTotal ? WEBSITE_REFRESH_KEYS_HELPER_TEXT : null,
        metadata,
        refreshedKeys,
        refreshedKeyCount,
        showRefreshedKeys,
      }
    }
    case 'failed': {
      const lines = [
        'Cache refresh failed. The website may show stale data until cache expires or refresh is retried.',
      ]
      const errorMessage = trimmedOrEmpty(details?.errorMessage)
      if (errorMessage) lines.push(errorMessage)
      return {
        status: resolved,
        statusLabel: 'Failed',
        statusColor: 'warning',
        lines,
        keyTypeBreakdown: null,
        keysHelperText: null,
        metadata,
        refreshedKeys,
        refreshedKeyCount,
        showRefreshedKeys,
      }
    }
    default:
      return {
        status: 'not_captured',
        statusLabel: 'Not captured',
        statusColor: 'grey',
        lines: ['Cache refresh details were not captured for this run.'],
        keyTypeBreakdown: null,
        keysHelperText: null,
        metadata: [],
        refreshedKeys: [],
        refreshedKeyCount: 0,
        showRefreshedKeys: false,
      }
  }
}

/**
 * Aggregate status chip for the combined "Website Publish & Refresh" stage.
 *
 * A failed refresh surfaces a warning, but never a hard failure when publish
 * succeeded (the inventory was published). Submitted/completed/skipped/not
 * captured never downgrade a successful publish — this is observability only.
 */
export function aggregateWebsitePublishRefreshStatus(input: {
  publishSucceeded: boolean
  publishFailed?: boolean
  refreshStatus: WebsiteRefreshStatus
}): { label: string; color: 'success' | 'warning' | 'grey' | 'error' } {
  if (input.publishFailed) return { label: 'Failed', color: 'error' }
  if (input.refreshStatus === 'failed') return { label: 'Warning', color: 'warning' }
  if (input.publishSucceeded && input.refreshStatus === 'completed') {
    return { label: 'Completed', color: 'success' }
  }
  if (input.publishSucceeded) return { label: 'Checked', color: 'success' }
  return { label: 'Not captured', color: 'grey' }
}

export function buildWebsitePublishRefreshSummaryLine(input: {
  visibility: Pick<WebsiteVisibilityChangesViewModel, 'activityComplete' | 'changedCount'>
  refresh: Pick<WebsiteRefreshViewModel, 'status' | 'refreshedKeyCount'>
}): string {
  const visibilityPart =
    !input.visibility.activityComplete || input.visibility.changedCount === null
      ? 'Website visibility changes unavailable'
      : input.visibility.changedCount > 0
        ? `${formatCount(input.visibility.changedCount, 'website visibility change')}`
        : 'No website visibility changes'

  let refreshPart: string
  switch (input.refresh.status) {
    case 'completed':
      refreshPart =
        input.refresh.refreshedKeyCount > 0
          ? `${formatCount(input.refresh.refreshedKeyCount, 'cache key')} refreshed`
          : 'cache refresh completed'
      break
    case 'failed':
      refreshPart = 'cache refresh failed'
      break
    case 'skipped':
      refreshPart = 'cache refresh not required'
      break
    case 'submitted':
      refreshPart = 'cache refresh submitted'
      break
    default:
      refreshPart = 'cache refresh not captured'
      break
  }

  return `${visibilityPart} · ${refreshPart}.`
}
